import { Button } from '../../components/ui/button.jsx'
import { Input } from '../../components/ui/input.jsx'
import { Label } from '../../components/ui/label.jsx'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getCookie } from '../../commonUtils/cookie.helper.js';
import { performLoginAction, handleSSOLoginAction } from '../../store/auth/actions.js';
import {EyeIcon, EyeSlashIcon, UserIcon} from '@heroicons/react/24/solid'
import { decrypt } from '../../commonUtils/crypto.helper.js';
import { getDefaultPageByRole } from '../../commonUtils/authUtils.js';


export default function Login() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const auth_local = getCookie("authToken");
    const isAuthorized = ( auth_local );
    
    const [formData, setFormdata] = useState({email: "", password: ""});
    const [errs, setErrs] = useState({email: "", password: ""});
    const [rememberme, setRememberme]=useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const { errors, errorMessage } = useSelector((state) => state.Auth);


    useEffect(() => {
        if(isAuthorized){
            getDefaultPageByRole(navigate);
        }
    }, [isAuthorized, navigate]);

    useEffect(() => {
        const rememberMeData = getCookie('rememberXanadu');
        if (rememberMeData) {
            const { storedEmail } = JSON.parse(decrypt(rememberMeData));
            setFormdata(prevState => ({...prevState, email: storedEmail || ""}));
            setRememberme(true);
        }
    }, []);

    const handleInputChange = (key, value) => {
        setFormdata({...formData, [key]: value});
    };

    const validateField = (fieldName, value) => {
        switch (fieldName) {
            case 'email': {
                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!value) {
                    return { valid: false, message: 'Email is required' };
                } else if (!emailPattern.test(value)) {
                    return { valid: false, message: 'Invalid email format' };
                }
                return { valid: true };
            }
            case 'password': {
                if (!value) {
                    return { valid: false, message: 'Password is required' };
                }
                if (value.length < 8) {
                    return { valid: false, message: 'Password must be at least 8 characters' };
                }
                if (!/[a-zA-Z]/.test(value)) {
                    return { valid: false, message: 'Password must include at least one letter' };
                }
                if (!/\d/.test(value)) {
                    return { valid: false, message: 'Password must include at least one number' };
                }
                if (!/[!@#$%^&*()?_+]/.test(value)) {
                    return { valid: false, message: 'Password must include at least one special character (!@#$%^&*()?_+)' };
                }
                return { valid: true };
            }
            default:
                return { valid: true };
        }
    };


    const loginSubmit = async (event) => {
        event.preventDefault();

        const newErrs = {};
        Object.keys(formData).forEach(key => {
            const { valid, message } = validateField(key, formData[key]);
            if (!valid) {
                newErrs[key] = message || `Please enter a valid ${key}.`;
            }
        });

        setErrs(newErrs);

        const isValid = Object.keys(newErrs).length === 0;
        if (isValid) {
            const params = { navigate, rememberme, formData };
            dispatch(performLoginAction(params));
        }
    };

    const handleSSOLogin = async (e) => {
        e.preventDefault();
        dispatch(handleSSOLoginAction({ type: 'redirect' }));
    };
    
    const renderError = (err) => {
        if (!err) return null;
        if (typeof err === 'string') return err;
        return err.message || JSON.stringify(err);
    };

    return (
        <>
            {/* <img src='/ixologo.png' alt='companyLogo' className='h-10 xl:h-12 w-auto mb-3'/> */}
            <h1 className='text-xl uppercase tracking-wider'>Xanadu</h1>
            <h1 className='text-2xl font-semibold mb-3'>Sign in to your account</h1>
            <p id="errdsp" className={`text-red-500 sm:mx-auto sm:w-full sm:max-w-sm border rounded-md text-center text-sm p-2 bg-gray-50 mt-4 ${renderError(errorMessage) ? 'block' : 'hidden'}`}>
                {renderError(errorMessage)}
            </p>

            <form className='flex flex-col space-y-4 w-full max-w-sm pt-3' onSubmit={loginSubmit}>
                <div className="space-y-1">
                    <Label htmlFor="username">Email / User ID</Label>
                    <div className="relative">
                        <Input
                            required
                            id="email"
                            name="email"
                            type="text"
                            autoComplete="email"
                            value={formData.email || ""}
                            onChange={e => handleInputChange("email", e.target.value)}
                            className="bg-white dark:bg-secondary focus-visible:ring-primary-foreground"
                        />
                        <div className="absolute inset-y-0 right-2 flex items-center text-gray-300 border-l ps-1.5">
                            <UserIcon className='h-5 w-auto'/>
                        </div>
                    </div>
                    {(errs?.email || errors?.email) && <p className="text-xs m-0 p-0 text-red-500">{renderError(errs?.email || errors?.email)}</p>}
                </div>
                <div className="space-y-1">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                        <Input
                            required
                            id="password"
                            name="password"
                            type={!showPassword?'password':'text'}
                            autoComplete="current-password"
                            value={formData.password || ""}
                            onChange={e => handleInputChange("password", e.target.value)}
                            className="bg-white dark:bg-secondary focus-visible:ring-primary-foreground"
                        />
                        <div
                            className="absolute inset-y-0 right-2 flex items-center text-gray-300 border-l ps-1.5 z-20"
                            onClick={()=>setShowPassword(!showPassword)}
                        >
                            {!showPassword?<EyeIcon className='h-5 w-auto'/>:<EyeSlashIcon className='h-5 w-auto'/>}
                        </div>
                    </div>
                    {(errs?.password || errors?.password) && <p className="text-xs m-0 p-0 text-red-500">{renderError(errs?.password || errors?.password)}</p>}
                </div>
                <div className="flex items-center justify-between pt-0.5 pb-1">
                    <Label htmlFor="password" onClick={()=>setRememberme(!rememberme)} className="flex items-center cursor-default lg:cursor-pointer text-sm leading-6">
                        <input
                            id="remember_me"
                            name="remember_me"
                            type="checkbox"
                            checked={rememberme} 
                            onChange={(e) => setRememberme(e.target.checked)}
                            className="h-4 w-4 me-1.5 rounded border text-primary focus:ring-primary"
                        />Remember Me
                    </Label>
                    <div className="text-sm">
                        {/* <Link to="forgot-password" className="font-semibold text-primary">
                            Forgot password
                        </Link> */}
                    </div>
                </div>
                <Button type="submit" className="w-full" >Sign In</Button>
                <Button variant="outline" className="w-full" onClick={handleSSOLogin} >
                    <img src='./microsoft_logo.png' alt='login' className='w-[20px] mr-2' />
                    Sign In with Microsoft
                </Button>
            </form>
        </>
    )
}